import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import {
    Row, Col, Button, Input, Card, CardBody, FormGroup, Table,
    Modal, ModalHeader, ModalBody, ModalFooter, Form, Label, Badge
} from 'reactstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation } from 'react-multi-lang';
import {
    faPen,
    faPlus,
    faTrash,
    faRefresh,
    faWrench,
    faCubes,
    faTractor,
    faCheckCircle,
    faListCheck,
    faChevronDown,
    faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import MachineTypesService from '../Services/MachineTypesService';
import { DionysosService } from '../Services/DionysosService';
import AuthService from '../Services/AuthService';

const getAssignedCodes = (codes) => {
    if (Array.isArray(codes)) return codes.filter(Boolean);
    if (typeof codes === 'string') return codes.split(',').map(c => c.trim()).filter(Boolean);
    return [];
};

const MachineTypes = ({ t }) => {
    const authService = new AuthService();
    const machineTypesService = new MachineTypesService();
    const dionysosService = new DionysosService();
    const { addToast } = useToasts();

    const [availableConfigs, setAvailableConfigs] = useState(() => authService.getAvailableConfigs() || []);
    const [selectedWh, setSelectedWh] = useState(() => authService.getConfig()?.whCode || '4');
    const [machineTypes, setMachineTypes] = useState([]);
    const [allMachines, setAllMachines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedMachines, setExpandedMachines] = useState({});

    // Machine Type Modal
    const [typeModalOpen, setTypeModalOpen] = useState(false);
    const [isEditType, setIsEditType] = useState(false);
    const [currentType, setCurrentType] = useState({
        id: null,
        name: '',
        description: '',
        subcategory: '',
        category: '',
        wh: '4',
        machineCodes: []
    });

    // Work Type Modal
    const [workTypeModalOpen, setWorkTypeModalOpen] = useState(false);
    const [isEditWorkType, setIsEditWorkType] = useState(false);
    const [selectedMachineTypeId, setSelectedMachineTypeId] = useState(null);
    const [currentWorkType, setCurrentWorkType] = useState({
        id: null,
        name: '',
        description: '',
        order: 1
    });

    useEffect(() => {
        const handleWhChange = (e) => {
            const newWh = e?.detail?.whCode || authService.getConfig()?.whCode;
            if (newWh && newWh !== selectedWh) {
                setSelectedWh(newWh);
            }
        };
        window.addEventListener('warehouseChanged', handleWhChange);
        window.addEventListener('storage', handleWhChange);
        return () => {
            window.removeEventListener('warehouseChanged', handleWhChange);
            window.removeEventListener('storage', handleWhChange);
        };
    }, [selectedWh]);

    useEffect(() => {
        loadData(selectedWh);
    }, [selectedWh]);

    const loadData = (wh = selectedWh) => {
        setLoading(true);
        const targetWh = wh || selectedWh || authService.getConfig()?.whCode;
        return Promise.all([
            machineTypesService.getMachineTypes(targetWh).catch(err => {
                console.error('Error fetching machine types:', err);
                return [];
            }),
            dionysosService.getMachines(targetWh).catch(err => {
                console.error('Error fetching machines:', err);
                return [];
            })
        ]).then(([typesRes, machinesRes]) => {
            const types = Array.isArray(typesRes) ? typesRes : (Array.isArray(typesRes?.data) ? typesRes.data : []);
            const machines = Array.isArray(machinesRes) ? machinesRes : (Array.isArray(machinesRes?.data) ? machinesRes.data : []);
            setMachineTypes(types);
            setAllMachines(machines);
            return { types, machines };
        }).catch(e => {
            addToast(e.message || 'Error loading machine types', { appearance: 'error', autoDismiss: true });
        }).finally(() => {
            setLoading(false);
        });
    };

    const loadMachineTypes = (wh = selectedWh) => {
        const targetWh = wh || selectedWh || authService.getConfig()?.whCode;
        return machineTypesService.getMachineTypes(targetWh)
            .then(res => {
                const types = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
                setMachineTypes(types);
                return types;
            })
            .catch(e => addToast(e.message || 'Error loading machine types', { appearance: 'error', autoDismiss: true }));
    };

    const handleSeedDefaults = async () => {
        setLoading(true);
        try {
            await machineTypesService.seedDefaults(selectedWh);
            addToast(t('machineTypes.seedDefaultsSuccess') || 'Default machine types loaded successfully.', { appearance: 'success', autoDismiss: true });
            await loadData(selectedWh);
        } catch (err) {
            addToast(err.message || 'Error loading default machine types', { appearance: 'error', autoDismiss: true });
            setLoading(false);
        }
    };

    const handleExtractFromMachines = async () => {
        setLoading(true);
        try {
            await machineTypesService.extractFromMachines(selectedWh);
            addToast(t('machineTypes.extractSuccess') || 'Machine types extracted from machines list successfully.', { appearance: 'success', autoDismiss: true });
            await loadData(selectedWh);
        } catch (err) {
            addToast(err.message || 'Error extracting machine types', { appearance: 'error', autoDismiss: true });
            setLoading(false);
        }
    };

    // --- Machine Options for react-select ---
    const safeMachines = Array.isArray(allMachines) ? allMachines : [];
    const machineOptions = safeMachines.filter(Boolean).map(m => ({
        value: m.Code,
        label: `${m.Code} - ${m.Description || ''}${m.Code1 ? ` (${m.Code1})` : ''}`
    }));

    const machineMap = new Map();
    safeMachines.filter(Boolean).forEach(m => {
        if (m.Code) {
            machineMap.set(m.Code, m.Description || m.Code);
        }
    });

    const toggleMachinesExpand = (typeId) => {
        setExpandedMachines(prev => ({
            ...prev,
            [typeId]: !prev[typeId]
        }));
    };

    // --- Machine Type Handlers ---
    const openAddTypeModal = () => {
        setCurrentType({
            id: null,
            name: '',
            description: '',
            subcategory: '',
            category: '',
            wh: selectedWh || authService.getConfig()?.whCode || '4',
            machineCodes: []
        });
        setIsEditType(false);
        setTypeModalOpen(true);
    };

    const openEditTypeModal = (type) => {
        const codes = getAssignedCodes(type?.machineCodes);
        setCurrentType({
            id: type.id,
            name: type.name || '',
            description: type.description || '',
            subcategory: type.subcategory || '',
            category: type.category || '',
            wh: type.wh || selectedWh || authService.getConfig()?.whCode || '4',
            machineCodes: codes
        });
        setIsEditType(true);
        setTypeModalOpen(true);
    };

    const handleSaveType = (e) => {
        e.preventDefault();
        if (!currentType.name || !currentType.name.trim()) {
            addToast('Name is required', { appearance: 'warning', autoDismiss: true });
            return;
        }

        const payload = {
            name: currentType.name.trim(),
            description: currentType.description || '',
            subcategory: currentType.subcategory || '',
            category: currentType.category || '',
            wh: currentType.wh || selectedWh || authService.getConfig()?.whCode,
            machineCodes: currentType.machineCodes
        };

        const promise = isEditType
            ? machineTypesService.updateMachineType(currentType.id, payload)
            : machineTypesService.createMachineType(payload);

        promise.then(() => {
            addToast(t('machineTypes.typeSaved') || 'Machine type saved successfully.', { appearance: 'success', autoDismiss: true });
            setTypeModalOpen(false);
            loadMachineTypes(selectedWh);
        }).catch(err => {
            addToast(err.message || 'Error saving machine type', { appearance: 'error', autoDismiss: true });
        });
    };

    const handleDeleteType = (id) => {
        if (window.confirm(t('machineTypes.confirmDelete') || 'Are you sure you want to delete this machine type?')) {
            machineTypesService.deleteMachineType(id).then(() => {
                addToast(t('machineTypes.typeDeleted') || 'Machine type deleted.', { appearance: 'success', autoDismiss: true });
                loadMachineTypes(selectedWh);
            }).catch(err => {
                addToast(err.message || 'Error deleting machine type', { appearance: 'error', autoDismiss: true });
            });
        }
    };

    // --- Work Type Handlers ---
    const openAddWorkTypeModal = (machineTypeId, currentWorkTypes = []) => {
        const wtList = Array.isArray(currentWorkTypes) ? currentWorkTypes : [];
        const nextOrder = wtList.length > 0
            ? Math.max(...wtList.map(w => w.order || 0)) + 1
            : 1;

        setSelectedMachineTypeId(machineTypeId);
        setCurrentWorkType({
            id: null,
            name: '',
            description: '',
            order: nextOrder
        });
        setIsEditWorkType(false);
        setWorkTypeModalOpen(true);
    };

    const openEditWorkTypeModal = (machineTypeId, wt) => {
        setSelectedMachineTypeId(machineTypeId);
        setCurrentWorkType({
            id: wt.id,
            name: wt.name || '',
            description: wt.description || '',
            order: wt.order !== undefined ? wt.order : 1
        });
        setIsEditWorkType(true);
        setWorkTypeModalOpen(true);
    };

    const handleSaveWorkType = (e) => {
        e.preventDefault();
        if (!currentWorkType.name || !currentWorkType.name.trim()) {
            addToast('Work type name is required', { appearance: 'warning', autoDismiss: true });
            return;
        }

        const payload = {
            name: currentWorkType.name.trim(),
            description: currentWorkType.description || '',
            order: Number(currentWorkType.order) || 1
        };

        const promise = isEditWorkType
            ? machineTypesService.updateWorkType(currentWorkType.id, payload)
            : machineTypesService.addWorkType(selectedMachineTypeId, payload);

        promise.then(() => {
            addToast(t('machineTypes.workTypeSaved') || 'Work type saved successfully.', { appearance: 'success', autoDismiss: true });
            setWorkTypeModalOpen(false);
            loadMachineTypes();
        }).catch(err => {
            addToast(err.message || 'Error saving work type', { appearance: 'error', autoDismiss: true });
        });
    };

    const handleDeleteWorkType = (workTypeId) => {
        if (window.confirm(t('machineTypes.confirmDeleteWork') || 'Are you sure you want to delete this work type?')) {
            machineTypesService.deleteWorkType(workTypeId).then(() => {
                addToast(t('machineTypes.workTypeDeleted') || 'Work type deleted.', { appearance: 'success', autoDismiss: true });
                loadMachineTypes();
            }).catch(err => {
                addToast(err.message || 'Error deleting work type', { appearance: 'error', autoDismiss: true });
            });
        }
    };

    // Filtered machine types
    const safeTypes = Array.isArray(machineTypes) ? machineTypes : [];
    const filteredTypes = safeTypes.filter(mt => {
        if (!mt) return false;
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        const nameMatch = (mt.name || '').toLowerCase().includes(q);
        const descMatch = (mt.description || '').toLowerCase().includes(q);
        const subcatMatch = (mt.subcategory || '').toLowerCase().includes(q);
        const catMatch = (mt.category || '').toLowerCase().includes(q);
        const codesStr = Array.isArray(mt.machineCodes) ? mt.machineCodes.join(',') : (mt.machineCodes || '');
        const codesMatch = codesStr.toLowerCase().includes(q);
        const workMatch = (Array.isArray(mt.workTypes) ? mt.workTypes : []).some(w => (w?.name || '').toLowerCase().includes(q));
        return nameMatch || descMatch || subcatMatch || catMatch || codesMatch || workMatch;
    });

    return (
        <div className="container-fluid py-3">
            {/* Page Header */}
            <Row className="align-items-center mb-3">
                <Col md={7}>
                    <h3 className="mb-1" style={{ color: '#2c3e50', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faCubes} className="mr-2 text-primary" />
                        {t('machineTypes.title') || 'Machine Types & Work Types'}
                    </h3>
                    <p className="text-muted mb-0">
                        {t('machineTypes.description') || 'Manage machine types and the allowed work types for each type'}
                    </p>
                </Col>
                <Col md={5} className="text-right d-flex justify-content-end align-items-center flex-wrap gap-2">
                    <Button color="secondary" outline className="mr-2" onClick={() => loadData(selectedWh)} disabled={loading} title={t('refresh') || 'Refresh'}>
                        <FontAwesomeIcon icon={faRefresh} spin={loading} />
                    </Button>
                    <Button color="success" outline className="mr-2" onClick={handleExtractFromMachines} disabled={loading} title={t('machineTypes.extractFromMachines') || 'Extract Types from Machines'}>
                        <FontAwesomeIcon icon={faListCheck} className="mr-1" />
                        {t('machineTypes.extractFromMachines') || 'Extract Types'}
                    </Button>
                    <Button color="primary" onClick={openAddTypeModal}>
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        {t('machineTypes.addType') || 'Add Machine Type'}
                    </Button>
                </Col>
            </Row>

            {/* Filter Bar: Warehouse & Search */}
            <Row className="mb-4 align-items-center">
                <Col md={4} className="mb-2 mb-md-0">
                    <FormGroup className="mb-0 d-flex align-items-center">
                        <Label for="whFilter" className="mb-0 mr-2 text-nowrap font-weight-bold" style={{ minWidth: '70px' }}>
                            {t('machineTypes.warehouse') || 'Warehouse'}:
                        </Label>
                        <Input
                            id="whFilter"
                            type="select"
                            value={selectedWh}
                            onChange={(e) => setSelectedWh(e.target.value)}
                            className="form-control-sm custom-select"
                        >
                            <option value="all">{t('machineTypes.allWarehouses') || 'All Warehouses'}</option>
                            {availableConfigs.map(c => (
                                <option key={c.whCode} value={c.whCode}>
                                    {c.name || c.whCode} ({c.whCode})
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={5}>
                    <Input
                        type="text"
                        placeholder={t('machineTypes.searchPlaceholder') || 'Search machine types...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </Col>
            </Row>

            {/* List of Machine Types */}
            {loading ? (
                <Card className="shadow-sm">
                    <CardBody className="text-center py-5 text-muted">
                        <FontAwesomeIcon icon={faRefresh} spin size="2x" className="mb-3 text-primary" />
                        <h5>{t('loading') || 'Loading...'}</h5>
                    </CardBody>
                </Card>
            ) : filteredTypes.length === 0 ? (
                <Card className="shadow-sm">
                    <CardBody className="text-center py-5 text-muted">
                        <FontAwesomeIcon icon={faCubes} size="3x" className="mb-3 text-secondary" style={{ opacity: 0.5 }} />
                        <h4 className="font-weight-bold mb-2">{t('machineTypes.noMachineTypes') || 'No machine types found.'}</h4>
                        <p className="mb-4 text-muted mx-auto" style={{ maxWidth: '600px' }}>
                            {t('machineTypes.emptyPrompt') || 'You can initialize standard machine types (Wire Saws, Drills, Loaders, Excavators, Trucks) with their allowed work types, or create a custom type.'}
                        </p>
                        <div className="d-flex justify-content-center" style={{ gap: '10px' }}>
                            <Button color="success" onClick={handleExtractFromMachines} disabled={loading} className="px-3">
                                <FontAwesomeIcon icon={faListCheck} className="mr-2" />
                                {t('machineTypes.extractFromMachines') || 'Extract Types from Machines'}
                            </Button>
                            <Button color="primary" onClick={openAddTypeModal} className="px-3">
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                {t('machineTypes.addType') || 'Add Machine Type'}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            ) : (
                filteredTypes.map(type => {
                    const assignedCodes = getAssignedCodes(type?.machineCodes);
                    const workTypesList = Array.isArray(type?.workTypes) ? type.workTypes : [];

                    return (
                        <Card key={type.id} className="shadow-sm mb-4" style={{ borderRadius: '8px', borderLeft: '4px solid #007bff' }}>
                            <CardBody>
                                <Row className="align-items-start">
                                    <Col md={7}>
                                        <div className="d-flex align-items-center mb-2 flex-wrap" style={{ gap: '6px' }}>
                                            <h4 className="mb-0 mr-2" style={{ fontWeight: '600', color: '#1a252f' }}>
                                                {type.name}
                                            </h4>
                                            {type.subcategory && (
                                                <Badge color="primary" pill className="px-2 py-1">
                                                    {type.subcategory}
                                                </Badge>
                                            )}
                                            {type.category && (
                                                <Badge color="info" pill className="px-2 py-1">
                                                    {type.category}
                                                </Badge>
                                            )}
                                            {type.wh && (
                                                <Badge color="secondary" pill className="px-2 py-1">
                                                    <FontAwesomeIcon icon={faCubes} className="mr-1" />
                                                    {availableConfigs.find(c => c.whCode === type.wh)?.name || `WH: ${type.wh}`}
                                                </Badge>
                                            )}
                                            {assignedCodes.length > 0 ? (
                                                <Button
                                                    color="light"
                                                    size="sm"
                                                    className="p-1 px-2 border d-inline-flex align-items-center text-dark"
                                                    style={{ borderRadius: '12px', fontSize: '12px', lineHeight: '1.2' }}
                                                    onClick={() => toggleMachinesExpand(type.id)}
                                                    title={expandedMachines[type.id] ? (t('machineTypes.hideMachines') || 'Hide Machines') : (t('machineTypes.showMachines') || 'Show Machines')}
                                                >
                                                    <FontAwesomeIcon icon={faTractor} className="mr-1 text-primary" />
                                                    <span className="mr-1 font-weight-bold">{assignedCodes.length}</span>
                                                    <span className="mr-1">{t('sideBar.machines') || 'Machines'}</span>
                                                    <FontAwesomeIcon icon={expandedMachines[type.id] ? faChevronUp : faChevronDown} size="xs" />
                                                </Button>
                                            ) : (
                                                <Badge color="secondary" pill className="px-2 py-1">
                                                    <FontAwesomeIcon icon={faTractor} className="mr-1" />
                                                    0 {t('sideBar.machines') || 'Machines'}
                                                </Badge>
                                            )}
                                        </div>
                                        {type.description && (
                                            <p className="text-muted mb-2 small">{type.description}</p>
                                        )}
                                        {assignedCodes.length > 0 && expandedMachines[type.id] && (
                                            <div className="mt-2 mb-2 p-2 bg-light rounded border">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <small className="text-muted font-weight-bold">
                                                        {t('machineTypes.assignedMachines') || 'Assigned Machines'} ({assignedCodes.length}):
                                                    </small>
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="p-0 text-muted"
                                                        style={{ fontSize: '11px', textDecoration: 'none' }}
                                                        onClick={() => toggleMachinesExpand(type.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faChevronUp} className="mr-1" />
                                                        {t('machineTypes.hideMachines') || 'Hide'}
                                                    </Button>
                                                </div>
                                                <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                                    {assignedCodes.map(code => (
                                                        <span
                                                            key={code}
                                                            className="badge badge-light border text-dark px-2 py-1"
                                                            title={machineMap.get(code) || code}
                                                            style={{ fontSize: '11px' }}
                                                        >
                                                            {code} {machineMap.get(code) ? `(${machineMap.get(code)})` : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={5} className="text-right">
                                        <Button
                                            color="info"
                                            size="sm"
                                            outline
                                            className="mr-2"
                                            onClick={() => openEditTypeModal(type)}
                                        >
                                            <FontAwesomeIcon icon={faPen} className="mr-1" />
                                            {t('machineTypes.editType') || 'Edit'}
                                        </Button>
                                        <Button
                                            color="danger"
                                            size="sm"
                                            outline
                                            onClick={() => handleDeleteType(type.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                                            {t('machineTypes.deleteType') || 'Delete'}
                                        </Button>
                                    </Col>
                                </Row>

                                <hr className="my-3" />

                                {/* Work Types Sub-Section */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 text-secondary" style={{ fontWeight: '600' }}>
                                        <FontAwesomeIcon icon={faListCheck} className="mr-2 text-success" />
                                        {t('machineTypes.workTypes') || 'Allowed Work Types'} ({workTypesList.length})
                                    </h6>
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => openAddWorkTypeModal(type.id, workTypesList)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                        {t('machineTypes.addWorkType') || 'Add Work Type'}
                                    </Button>
                                </div>

                                {workTypesList.length === 0 ? (
                                    <p className="text-muted small mb-0 font-italic py-2">
                                        {t('machineTypes.noWorkTypes') || 'No work types configured for this machine type.'}
                                    </p>
                                ) : (
                                    <Table responsive hover size="sm" className="mb-0 mt-2">
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: '60px' }}>{t('machineTypes.order') || '#' || 'Order'}</th>
                                                <th>{t('machineTypes.workTypeName') || 'Work Type'}</th>
                                                <th>{t('machineTypes.workTypeDescription') || 'Description'}</th>
                                                <th className="text-right" style={{ width: '120px' }}>{t('machineTypes.actions') || 'Actions'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {workTypesList.map(wt => (
                                                <tr key={wt.id}>
                                                    <td className="align-middle font-weight-bold text-muted">{wt.order || '-'}</td>
                                                    <td className="align-middle font-weight-bold" style={{ color: '#2c3e50' }}>
                                                        <FontAwesomeIcon icon={faCheckCircle} className="text-success mr-2" />
                                                        {wt.name}
                                                    </td>
                                                    <td className="align-middle text-muted">{wt.description || '-'}</td>
                                                    <td className="align-middle text-right">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            outline
                                                            className="mr-1 py-0 px-2"
                                                            onClick={() => openEditWorkTypeModal(type.id, wt)}
                                                            title={t('machineTypes.editWorkType') || 'Edit'}
                                                        >
                                                            <FontAwesomeIcon icon={faPen} size="xs" />
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            outline
                                                            className="py-0 px-2"
                                                            onClick={() => handleDeleteWorkType(wt.id)}
                                                            title={t('machineTypes.deleteWorkType') || 'Delete'}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} size="xs" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </CardBody>
                        </Card>
                    );
                })
            )}

            {/* Modal: Create / Edit Machine Type */}
            <Modal isOpen={typeModalOpen} toggle={() => setTypeModalOpen(!typeModalOpen)} size="lg">
                <Form onSubmit={handleSaveType}>
                    <ModalHeader toggle={() => setTypeModalOpen(!typeModalOpen)}>
                        <FontAwesomeIcon icon={isEditType ? faPen : faPlus} className="mr-2 text-primary" />
                        {isEditType
                            ? (t('machineTypes.editType') || 'Edit Machine Type')
                            : (t('machineTypes.addType') || 'Add Machine Type')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="typeName">
                                {t('machineTypes.typeName') || 'Type Name'} <span className="text-danger">*</span>
                            </Label>
                            <Input
                                id="typeName"
                                type="text"
                                required
                                placeholder="e.g. ΦΟΡΤΩΤΕΣ, ΣΥΡΜΑΤΟΚΟΠΕΣ, ΕΚΣΚΑΦΕΙΣ..."
                                value={currentType.name}
                                onChange={(e) => setCurrentType({ ...currentType, name: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="typeDescription">
                                {t('machineTypes.typeDescription') || 'Description'}
                            </Label>
                            <Input
                                id="typeDescription"
                                type="textarea"
                                rows="2"
                                placeholder="e.g. Μηχανήματα κοπής / φόρτωσης..."
                                value={currentType.description}
                                onChange={(e) => setCurrentType({ ...currentType, description: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="typeSubcategory">
                                {t('machineTypes.subcategory') || 'Subcategory (Code2)'}
                            </Label>
                            <Input
                                id="typeSubcategory"
                                type="text"
                                placeholder="e.g. ΣΥΡΜΑΤΟΚΟΠΗ, ΦΟΡΤΩΤΕΣ, ΧΩΜΑΤΟΥΡΓΙΚΑ, ΠΕΡΟΝΟΦΟΡΑ..."
                                value={currentType.subcategory}
                                onChange={(e) => setCurrentType({ ...currentType, subcategory: e.target.value })}
                            />
                            <small className="text-muted">
                                Match subcategory from ERP (Code2) to assign tasks to machines with this subcategory.
                            </small>
                        </FormGroup>

                        <FormGroup>
                            <Label for="typeCategory">
                                {t('machineTypes.category') || 'Category (Code1)'}
                            </Label>
                            <Input
                                id="typeCategory"
                                type="text"
                                placeholder="e.g. ΜΗΧΑΝΗΜΑΤΑ, ΦΟΡΤΗΓΑ, ΕΠΙΒΑΤΙΚΟ ΑΥΤΟΚΙΝΗΤΟ..."
                                value={currentType.category}
                                onChange={(e) => setCurrentType({ ...currentType, category: e.target.value })}
                            />
                            <small className="text-muted">
                                Category from ERP (Code1).
                            </small>
                        </FormGroup>

                        <FormGroup>
                            <Label for="typeWh">
                                {t('machineTypes.warehouse') || 'Warehouse'}
                            </Label>
                            <Input
                                id="typeWh"
                                type="select"
                                value={currentType.wh || ''}
                                onChange={(e) => setCurrentType({ ...currentType, wh: e.target.value })}
                            >
                                <option value="">{t('machineTypes.allWarehouses') || 'All Warehouses / Global'}</option>
                                {availableConfigs.map(c => (
                                    <option key={c.whCode} value={c.whCode}>
                                        {c.name || c.whCode} ({c.whCode})
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                {t('machineTypes.assignedMachines') || 'Assigned Machines'}
                            </Label>
                            <Select
                                isMulti
                                options={machineOptions}
                                placeholder={t('machineTypes.selectMachines') || 'Select machines...'}
                                value={machineOptions.filter(opt => Array.isArray(currentType.machineCodes) && currentType.machineCodes.includes(opt.value))}
                                onChange={(selected) => {
                                    const codes = selected ? selected.map(s => s.value) : [];
                                    setCurrentType({ ...currentType, machineCodes: codes });
                                }}
                            />
                            <small className="text-muted">
                                Select all machines that belong to this machine type.
                            </small>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setTypeModalOpen(false)}>
                            {t('machineTypes.cancel') || 'Cancel'}
                        </Button>
                        <Button color="primary" type="submit">
                            {t('machineTypes.save') || 'Save'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal: Create / Edit Work Type */}
            <Modal isOpen={workTypeModalOpen} toggle={() => setWorkTypeModalOpen(!workTypeModalOpen)}>
                <Form onSubmit={handleSaveWorkType}>
                    <ModalHeader toggle={() => setWorkTypeModalOpen(!workTypeModalOpen)}>
                        <FontAwesomeIcon icon={isEditWorkType ? faPen : faPlus} className="mr-2 text-success" />
                        {isEditWorkType
                            ? (t('machineTypes.editWorkType') || 'Edit Work Type')
                            : (t('machineTypes.addWorkType') || 'Add Work Type')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="workTypeName">
                                {t('machineTypes.workTypeName') || 'Work Type Name'} <span className="text-danger">*</span>
                            </Label>
                            <Input
                                id="workTypeName"
                                type="text"
                                required
                                placeholder="e.g. Κοπή, Φόρτωση Όγκων, Διάτρηση, Συντήρηση..."
                                value={currentWorkType.name}
                                onChange={(e) => setCurrentWorkType({ ...currentWorkType, name: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="workTypeDescription">
                                {t('machineTypes.workTypeDescription') || 'Description'}
                            </Label>
                            <Input
                                id="workTypeDescription"
                                type="text"
                                placeholder="e.g. Κοπή όγκων / παταριού..."
                                value={currentWorkType.description}
                                onChange={(e) => setCurrentWorkType({ ...currentWorkType, description: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="workTypeOrder">
                                {t('machineTypes.order') || 'Display Order'}
                            </Label>
                            <Input
                                id="workTypeOrder"
                                type="number"
                                min="1"
                                value={currentWorkType.order}
                                onChange={(e) => setCurrentWorkType({ ...currentWorkType, order: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setWorkTypeModalOpen(false)}>
                            {t('machineTypes.cancel') || 'Cancel'}
                        </Button>
                        <Button color="success" type="submit">
                            {t('machineTypes.save') || 'Save'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </div>
    );
};

export default withTranslation(MachineTypes);
